import { SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { JsonEditor } from 'json-edit-react'
import { useEffect, useState } from 'react'

/**
 * The raw `meta` jsonb, edited as a tree.
 *
 * The form above it owns the fields ATLAS understands; this owns everything else — the
 * keys the taxonomy load wrote, the ones a seed script added, the ones nobody has built a
 * field for yet. Without it those keys are readable on a Meta tab and editable nowhere, so
 * correcting one means a SQL statement.
 *
 * A tree rather than a textarea because `meta` is arbitrarily nested — a tagline is an
 * object, links are an array of objects — and typing valid JSON by hand to change one
 * scalar is a poor trade. The editor keeps types, so `false` cannot become `"false"`,
 * which a text field makes easy and which reads as truthy everywhere afterwards.
 */
export interface MetaJsonEditorProps {
	/** The stored object. Re-seeded from the parent, so a save resets the draft. */
	meta: Record<string, unknown>
	/** What the reader is looking at, for the copy — a brand, a product, or an axis value. */
	subject: string
	saving?: boolean
	onSave: (meta: Record<string, unknown>) => void
	/** Card heading. Two metas exist per subject, so each editor says which it edits. */
	title?: string
	description?: string
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b)

/**
 * An object to edit, whatever arrived.
 *
 * `meta` is a jsonb column with no schema behind it, so a caller can hand over null, or a
 * page can render before its read lands. The tree throws on anything that is not an
 * object, which would take the whole tab down rather than showing an empty editor.
 */
const objectOf = (value: unknown): Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}

export function MetaJsonEditor({
	meta,
	subject,
	saving,
	onSave,
	title = 'Raw meta',
	description,
}: MetaJsonEditorProps) {
	const [draft, setDraft] = useState<Record<string, unknown>>(() => objectOf(meta))
	const [touched, setTouched] = useState(false)

	// Re-seed from the server's copy whenever it changes, but never over an edit in
	// progress — a refetch mid-edit would otherwise discard what was changed.
	useEffect(() => {
		if (!touched) setDraft(objectOf(meta))
	}, [meta, touched])

	const dirty = !same(draft, objectOf(meta))
	const keys = Object.keys(draft).length

	return (
		<SectionCard
			title={title}
			description={description ?? `Everything stored on this ${subject} that the fields above do not cover.`}
		>
			<div className="rounded-ds-control border border-ds-border p-2">
				<JsonEditor
					data={draft}
					rootName=""
					// Every change is a draft until Save: the parent owns the write, and an
					// edit-as-you-type tree would fire one request per keystroke.
					setData={(next) => {
						setTouched(true)
						setDraft(next as Record<string, unknown>)
					}}
					// Whole-object replacement belongs to Save/Revert, not to a row control.
					restrictDelete={({ level }) => level === 0}
					minWidth="100%"
					maxWidth="100%"
				/>
			</div>
			<p className="mt-1 text-ds-text-subtle text-xs">
				{keys} field{keys === 1 ? '' : 's'}
				{dirty ? ' · unsaved' : ''}
			</p>
			<div className="mt-3 flex justify-end gap-2">
				<Button
					type="button"
					variant="secondary"
					className="rounded-ds-full"
					isDisabled={saving || !dirty}
					onPress={() => {
						setTouched(false)
						setDraft(objectOf(meta))
					}}
				>
					Revert
				</Button>
				<Button
					type="button"
					className="rounded-ds-full px-5"
					isDisabled={saving || !dirty}
					onPress={() => {
						setTouched(false)
						onSave(draft)
					}}
				>
					{saving ? 'Saving…' : 'Save meta'}
				</Button>
			</div>
		</SectionCard>
	)
}
